import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Target, CheckCircle2, Trophy, Loader2, ArrowRight, ArrowLeft, RotateCcw, Brain } from "lucide-react";
import { prepOverview, prepGenerate, prepSubmit, prepLeaderboard } from "@/lib/prepApi";

const TRACK_ACCENT = {
  aptitude: "#C2410C", reasoning: "#7C3AED", verbal: "#0a66c2", technical: "#15803D", hr: "#D97706",
};

export default function PrepPage() {
  const [ov, setOv] = useState(null);
  const [lb, setLb] = useState(null);
  const [session, setSession] = useState(null); // active practice session

  const load = async () => {
    try { const { data } = await prepOverview(); setOv(data); } catch (_) {}
    try { const { data } = await prepLeaderboard(); setLb(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  if (session) {
    return <PracticeSession session={session} onDone={() => { setSession(null); load(); }} onExit={() => setSession(null)} />;
  }

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Placement prep</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Practice daily. Watch your score climb.</h1>
      <p className="text-stone-500 mt-2 max-w-lg leading-relaxed">AI-generated questions tuned to your branch and target role. Every set builds your streak — and your Wishire Score.</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Flame} label="Day streak" value={ov?.streak ?? 0} accent="#C2410C" hint={ov?.practiced_today ? "Done today ✓" : "Practice to keep it alive"} />
        <Stat icon={Target} label="Accuracy" value={`${ov?.accuracy ?? 0}%`} accent="#15803D" hint={`${ov?.correct ?? 0}/${ov?.solved ?? 0} correct`} />
        <Stat icon={CheckCircle2} label="Solved" value={ov?.solved ?? 0} accent="#7C3AED" hint="Total questions" />
        <Stat icon={Trophy} label="Prep points" value={ov?.points ?? 0} accent="#D97706" hint={lb?.your_rank ? `Rank #${lb.your_rank} in batch` : "Earn 1 per correct"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        {/* Tracks */}
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl mb-4">Choose a track</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(ov?.tracks || []).map((t) => {
              const bt = ov?.by_track?.[t.key];
              return (
                <button key={t.key} onClick={() => setSession({ track: t.key, label: t.label })} data-testid={`prep-track-${t.key}`}
                  className="text-left rounded-3xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.3)] transition-all">
                  <span className="inline-flex p-2.5 rounded-2xl mb-4" style={{ background: `${TRACK_ACCENT[t.key]}15`, color: TRACK_ACCENT[t.key] }}><Brain size={20} /></span>
                  <h3 className="font-display text-xl">{t.label}</h3>
                  <p className="mt-1.5 text-sm text-stone-500 leading-relaxed">{t.desc}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-stone-400 font-mono">{bt ? `${bt.correct}/${bt.solved} solved` : "Not started"}</span>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#C2410C]">Practice <ArrowRight size={15} /></span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Leaderboard */}
        <div>
          <h2 className="font-display text-2xl mb-4">Batch leaderboard</h2>
          <div className="rounded-3xl border border-stone-200 bg-white p-5">
            {!lb ? <p className="text-stone-400 text-sm">Loading…</p> : !lb.joined ? (
              <p className="text-stone-500 text-sm">Join your college (Campus tab) to see how you rank against your batch.</p>
            ) : lb.rows.length === 0 ? (
              <p className="text-stone-500 text-sm">No practice yet in your batch — be the first on the board.</p>
            ) : (
              <ol className="space-y-2">
                {lb.rows.map((r) => (
                  <li key={r.rank} className={`flex items-center justify-between rounded-xl px-3 py-2 ${r.you ? "bg-amber-50 border border-amber-200" : ""}`}>
                    <span className="flex items-center gap-2.5 text-sm">
                      <span className={`font-mono w-6 text-center ${r.rank <= 3 ? "text-[#C2410C] font-bold" : "text-stone-400"}`}>{r.rank}</span>
                      <span className={r.you ? "font-semibold" : ""}>{r.name}{r.you ? " (you)" : ""}</span>
                    </span>
                    <span className="font-mono text-sm text-stone-600">{r.points}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, hint }) {
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-5">
      <span className="inline-flex p-2 rounded-xl mb-3" style={{ background: `${accent}15`, color: accent }}><Icon size={18} /></span>
      <p className="font-display text-3xl">{value}</p>
      <p className="overline mt-1">{label}</p>
      {hint && <p className="text-xs text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

/* ─────────── Practice session ─────────── */
function PracticeSession({ session, onDone, onExit }) {
  const [qs, setQs] = useState(null);
  const [i, setI] = useState(0);
  const [chosen, setChosen] = useState({});      // {index: optionIndex}
  const [revealed, setRevealed] = useState({});  // {index: true}
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await prepGenerate(session.track, 5); setQs(data.questions); }
      catch (e) { setErr(e?.response?.data?.detail || "Could not generate questions."); }
    })();
  }, [session.track]);

  if (err) return (
    <div className="container-x py-14">
      <button onClick={onExit} className="text-sm text-stone-500 inline-flex items-center gap-1 mb-4"><ArrowLeft size={15} /> Back</button>
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-amber-800">{err}</div>
    </div>
  );
  if (!qs) return <div className="container-x py-20 text-stone-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Generating {session.label} questions…</div>;

  const q = qs[i];
  const pick = (opt) => {
    if (revealed[i] != null) return;
    setChosen((c) => ({ ...c, [i]: opt }));
    setRevealed((r) => ({ ...r, [i]: true }));
  };

  const finish = async () => {
    setSaving(true);
    const results = qs.map((qq, idx) => ({ correct: chosen[idx] === qq.answer }));
    try { const { data } = await prepSubmit(session.track, results); setResult(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  if (result) {
    return (
      <div className="container-x py-14 max-w-xl">
        <div className="rounded-3xl border border-stone-200 bg-white p-8 text-center">
          <div className="inline-flex p-3 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><CheckCircle2 size={26} /></div>
          <h2 className="font-display text-4xl">{result.scored}/{result.of}</h2>
          <p className="text-stone-500 mt-1">on {session.label}</p>
          <div className="mt-6 flex justify-center gap-8 text-center">
            <div><p className="font-display text-2xl text-[#C2410C] inline-flex items-center gap-1"><Flame size={18} /> {result.streak}</p><p className="overline mt-1">Day streak</p></div>
            <div><p className="font-display text-2xl text-[#D97706]">{result.points}</p><p className="overline mt-1">Prep points</p></div>
            <div><p className="font-display text-2xl text-[#15803D]">{result.accuracy}%</p><p className="overline mt-1">Accuracy</p></div>
          </div>
          <div className="mt-7 flex gap-3 justify-center">
            <button onClick={onDone} className="btn-primary"><RotateCcw size={15} /> More practice</button>
          </div>
        </div>
      </div>
    );
  }

  const answered = revealed[i] != null;
  const allAnswered = qs.every((_, idx) => revealed[idx] != null);

  return (
    <div className="container-x py-10 md:py-14 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onExit} className="text-sm text-stone-500 inline-flex items-center gap-1"><ArrowLeft size={15} /> Exit</button>
        <span className="font-mono text-sm text-stone-400">{session.label} · {i + 1}/{qs.length}</span>
      </div>
      <div className="h-1.5 rounded-full bg-stone-200 mb-8 overflow-hidden"><div className="h-full bg-[#C2410C] transition-all" style={{ width: `${((i + 1) / qs.length) * 100}%` }} /></div>

      <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h2 className="font-display text-2xl leading-snug">{q.q}</h2>
        <div className="mt-5 space-y-2.5">
          {q.options.map((opt, oi) => {
            const isChosen = chosen[i] === oi;
            const isCorrect = q.answer === oi;
            let cls = "border-stone-300 bg-white hover:border-stone-900";
            if (answered) {
              if (isCorrect) cls = "border-emerald-400 bg-emerald-50";
              else if (isChosen) cls = "border-red-300 bg-red-50";
              else cls = "border-stone-200 bg-white opacity-70";
            }
            return (
              <button key={oi} onClick={() => pick(oi)} disabled={answered} data-testid={`prep-opt-${oi}`}
                className={`w-full text-left px-4 py-3.5 rounded-2xl border text-sm transition-colors ${cls}`}>
                <span className="font-mono text-xs text-stone-400 mr-2">{String.fromCharCode(65 + oi)}</span>{opt}
                {answered && isCorrect && <CheckCircle2 size={15} className="inline ml-2 text-emerald-600" />}
              </button>
            );
          })}
        </div>
        {answered && q.explanation && (
          <div className="mt-4 rounded-2xl bg-stone-100 border border-stone-200 px-4 py-3 text-sm text-stone-700"><strong>Why:</strong> {q.explanation}</div>
        )}
      </motion.div>

      <div className="mt-8 flex justify-between">
        <button disabled={i === 0} onClick={() => setI((v) => v - 1)} className="btn-ghost text-sm disabled:opacity-40"><ArrowLeft size={15} /> Prev</button>
        {i < qs.length - 1 ? (
          <button onClick={() => setI((v) => v + 1)} className="btn-primary text-sm">Next <ArrowRight size={15} /></button>
        ) : (
          <button onClick={finish} disabled={!allAnswered || saving} data-testid="prep-finish" className="btn-primary text-sm disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <>Finish <CheckCircle2 size={15} /></>}
          </button>
        )}
      </div>
    </div>
  );
}
